import React, { ReactElement, useState } from 'react';
import { useMutation } from 'react-apollo';
import { merge } from 'lodash';
import { Immutable } from '@wds/utils';
import { Button } from '@wds/button';
import { Box } from '@wds/box';
import { Link } from '@wds/link';
import { Sheet } from '@wds/sheet';
import { Typography } from '@wds/typography';
import { SkeletonLoader, InfiniteScrollTrigger } from '@sm/webassets';
import {
  useInProductMessagesQuery,
  InProductMessagesQuery,
} from './InProductMessages.generated';
import notificationsMarkAllAsRead from './NotificationsMarkAllAsRead.graphql';
import useStyles from './useStyles';
import NotificationItem from './NotificationItem';
import notificationZeroStateImage from '../../../static/images/notification-zero-state.svg';

const PAGE_SIZE = 10;

export type NotificationsSheetProps = Immutable<{
  showNotifications: boolean;
  unreadNotificationsCount: number;
}> & {
  toggleNotifications(): void;
};

const NotificationsSheet = ({
  showNotifications,
  toggleNotifications,
  unreadNotificationsCount,
}: NotificationsSheetProps): ReactElement => {
  const { notificationsContainer, notificationsList, notificationsEmpty } =
    useStyles();
  const [hasMoreToLoad, setHasMoreToLoad] = useState(true);

  const {
    loading,
    error,
    data,
    refetch: refetchMessages,
    fetchMore: fetchMoreMessages,
  } = useInProductMessagesQuery({
    variables: {
      input: {
        isDeleted: false,
        beforeMessageId: null,
        pageSize: PAGE_SIZE,
      },
    },
    skip: !showNotifications,
    fetchPolicy: 'network-only',
  });

  const successfullyMarkedAllAsRead = () => {
    refetchMessages();
    toggleNotifications();
  };

  const [
    markAllMessagesAsRead,
    { loading: loadingMarkAll, error: errorMarkAll },
  ] = useMutation(notificationsMarkAllAsRead, {
    onCompleted: successfullyMarkedAllAsRead,
  });

  const notificationsContent = data?.inProductMessages?.items;
  const notificationsLastMessageId =
    data?.inProductMessages?.cursorInfo.lastMessageId;
  const userHasNoMessages = notificationsContent?.length === 0;

  const showMarkAllAsRead =
    notificationsContent &&
    notificationsContent.length > 0 &&
    unreadNotificationsCount > 0;

  const clickMarkAllAsRead = () => {
    markAllMessagesAsRead({
      variables: {
        input: {
          notAfterDate,
        },
      },
    });
  };

  const updateMessages = (
    prevData: InProductMessagesQuery,
    { fetchMoreResult: newData }: { fetchMoreResult?: InProductMessagesQuery }
  ) => {
    if (!newData) {
      return prevData;
    }

    if (newData?.inProductMessages.items.length < PAGE_SIZE) {
      setHasMoreToLoad(false);
    } else {
      setHasMoreToLoad(true);
    }

    const combinedData = merge({}, prevData, newData, {
      inProductMessages: {
        items: prevData.inProductMessages.items.concat(
          newData.inProductMessages.items
        ),
      },
    });

    return combinedData;
  };

  const onLoadMore = () => {
    fetchMoreMessages({
      variables: {
        input: {
          beforeMessageId: notificationsLastMessageId,
          pageSize: PAGE_SIZE,
          isDeleted: false,
        },
      },
      updateQuery: updateMessages,
    });
  };

  const notAfterDate = notificationsContent && notificationsContent[0]?.sentAt;

  return (
    <Sheet
      show={showNotifications}
      onClose={toggleNotifications}
      showCloseButton
      offsetTop={48}
    >
      <Sheet.Header header="Notifications center" />
      <Sheet.Body padding={false}>
        <Box className={notificationsContainer}>
          <Choose>
            <When condition={userHasNoMessages}>
              <Box mx={6} my={10}>
                <Box className={notificationsEmpty}>
                  <img
                    src={notificationZeroStateImage}
                    alt="graphic of a notifications interface"
                  />
                </Box>
                <Typography
                  variant="body"
                  align="center"
                  component="p"
                  color="dark"
                  weight="medium"
                >
                  Watch the bell for updates
                </Typography>
                <Typography
                  variant="bodySm"
                  align="center"
                  component="p"
                  color="darkMuted"
                >
                  We&apos;ll notify you about new activity on your surveys or
                  account.{' '}
                  <a href="https://help.surveymonkey.com/en/surveymonkey/send/survey-notifications/">
                    Learn more
                  </a>
                </Typography>
              </Box>
            </When>
            <When condition={!!data && !userHasNoMessages}>
              {showMarkAllAsRead && (
                <Box m={4}>
                  <Typography align="right">
                    <Button
                      size="sm"
                      color="muted"
                      onClick={clickMarkAllAsRead}
                      loading={loadingMarkAll}
                    >
                      Mark all as read
                    </Button>
                  </Typography>
                  {errorMarkAll && (
                    <Box mx={6} my={6}>
                      <Typography
                        variant="bodySm"
                        align="center"
                        component="p"
                        color="darkMuted"
                      >
                        There was an error marking all notifications as read.
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
              <ul className={notificationsList}>
                {notificationsContent?.map(notification => (
                  <NotificationItem
                    key={notification.id}
                    id={notification.id}
                    sentAt={notification.sentAt}
                    read={notification.isRead}
                  >
                    {notification.content}
                  </NotificationItem>
                ))}
              </ul>
              <InfiniteScrollTrigger
                hasMoreToLoad={hasMoreToLoad}
                onLoadMore={onLoadMore}
                loadingFirstData={loading}
                loadingMore={loading}
              />
              {!hasMoreToLoad && (
                <Box mx={8} my={10}>
                  <Typography
                    variant="bodySm"
                    align="center"
                    component="p"
                    color="darkMuted"
                  >
                    These are all your notifications from the last 30 days.
                  </Typography>
                </Box>
              )}
            </When>
            <When condition={!!loading}>
              <Box m={5}>
                <SkeletonLoader height={100} width="100%">
                  <path
                    x="16"
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M0 5C0 2.23858 2.23858 0 5 0H81C83.7614 0 86 2.23858 86 5C86 7.76142 83.7614 10 81 10H5C2.23858 10 0 7.76142 0 5Z"
                    fill="#E5E6EA"
                  />
                  <path
                    x="16"
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M0 26C0 23.2386 2.23858 21 5 21H282C284.761 21 287 23.2386 287 26C287 28.7614 284.761 31 282 31H5C2.23858 31 0 28.7614 0 26Z"
                    fill="#B7B8BC"
                  />
                  <path
                    x="16"
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M0 47C0 44.2386 2.23858 42 5 42H144C146.761 42 149 44.2386 149 47C149 49.7614 146.761 52 144 52H5C2.23858 52 0 49.7614 0 47Z"
                    fill="#C1C3C9"
                  />
                </SkeletonLoader>
                <SkeletonLoader height={100} width="100%">
                  <path
                    x="16"
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M0 5C0 2.23858 2.23858 0 5 0H81C83.7614 0 86 2.23858 86 5C86 7.76142 83.7614 10 81 10H5C2.23858 10 0 7.76142 0 5Z"
                    fill="#E5E6EA"
                  />
                  <path
                    x="16"
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M0 26C0 23.2386 2.23858 21 5 21H282C284.761 21 287 23.2386 287 26C287 28.7614 284.761 31 282 31H5C2.23858 31 0 28.7614 0 26Z"
                    fill="#B7B8BC"
                  />
                  <path
                    x="16"
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M0 47C0 44.2386 2.23858 42 5 42H144C146.761 42 149 44.2386 149 47C149 49.7614 146.761 52 144 52H5C2.23858 52 0 49.7614 0 47Z"
                    fill="#C1C3C9"
                  />
                </SkeletonLoader>
                <SkeletonLoader height={100} width="100%">
                  <path
                    x="16"
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M0 5C0 2.23858 2.23858 0 5 0H81C83.7614 0 86 2.23858 86 5C86 7.76142 83.7614 10 81 10H5C2.23858 10 0 7.76142 0 5Z"
                    fill="#E5E6EA"
                  />
                  <path
                    x="16"
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M0 26C0 23.2386 2.23858 21 5 21H282C284.761 21 287 23.2386 287 26C287 28.7614 284.761 31 282 31H5C2.23858 31 0 28.7614 0 26Z"
                    fill="#B7B8BC"
                  />
                  <path
                    x="16"
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M0 47C0 44.2386 2.23858 42 5 42H144C146.761 42 149 44.2386 149 47C149 49.7614 146.761 52 144 52H5C2.23858 52 0 49.7614 0 47Z"
                    fill="#C1C3C9"
                  />
                </SkeletonLoader>
              </Box>
            </When>
            <When condition={!!error}>
              <Box my={10}>
                <Typography
                  variant="bodySm"
                  align="center"
                  component="p"
                  color="darkMuted"
                >
                  An error occured while loading your notifications.
                </Typography>
              </Box>
            </When>
          </Choose>
        </Box>
      </Sheet.Body>
      <Sheet.Footer align="left">
        <Link underline href="#">
          Share feedback
        </Link>
      </Sheet.Footer>
    </Sheet>
  );
};

export default NotificationsSheet;
export { NotificationsSheet };
